import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ChevronRight, Zap, Truck, ShieldCheck, Clock, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import NotFound from "@/pages/NotFound";

export default function PackageRoutePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/packages/route/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <NotFound message="Halaman paket tidak ditemukan." />;
  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-7xl px-4 py-20 text-muted-foreground">Memuat...</div>
      </div>
    );
  }

  const sendUrl = `/kirim-paket?asal=${encodeURIComponent(data.origin)}&tujuan=${encodeURIComponent(data.destination)}`;

  return (
    <div className="min-h-screen bg-background" data-testid="package-route-page">
      <Seo
        title={data.title}
        description={`Kirim dokumen & paket barang dari ${data.origin} ke ${data.destination} dengan Paket Kilat Travele. Penjemputan door to door, cepat, dan aman. Tarif dikonfirmasi Admin.`}
        schema={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: (data.faq || []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }}
      />
      <Navbar />
      <Breadcrumbs
        items={[
          { name: "Beranda", path: "/" },
          { name: "Paket Kilat" },
          { name: `${data.origin} → ${data.destination}` },
        ]}
      />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="flex items-center gap-2 font-mono-terminal text-xs uppercase tracking-widest text-primary">
          <Zap className="h-3.5 w-3.5" /> Paket Kilat Barang & Dokumen
        </div>
        <h1 className="mt-2 max-w-3xl font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl lg:text-5xl">
          {data.origin} <span className="text-primary">→</span> {data.destination}
        </h1>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          Kirim dokumen penting dan paket barang dari {data.origin} ke {data.destination} dengan layanan Paket Kilat Travele.
          Penjemputan door to door, cepat, dan aman. Tarif dikonfirmasi Admin sesuai berat dan jenis kiriman.
        </p>

        <Link to={sendUrl}>
          <Button data-testid="pkg-cta" className="mt-5 bg-primary font-bold text-primary-foreground hover:bg-primary/90">
            Kirim Paket Sekarang <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </Link>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            { icon: Truck, t: "Door to Door", d: "Dijemput dari lokasi Anda, diantar ke alamat tujuan." },
            { icon: Clock, t: "Cepat & Terjadwal", d: "Mengikuti keberangkatan driver antarkota." },
            { icon: ShieldCheck, t: "Aman & Terpantau", d: "Dikelola Admin Travele dari jemput hingga tiba." },
          ].map((f, i) => (
            <div key={i} className="border border-border bg-card p-5">
              <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-md bg-primary/15 text-primary"><f.icon className="h-5 w-5" /></div>
              <h3 className="font-display text-lg font-bold">{f.t}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{f.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mb-4 mt-12 font-display text-2xl font-bold tracking-tight">Pertanyaan Umum</h2>
        <Accordion type="single" collapsible className="border border-border bg-card px-4">
          {data.faq.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`pkg-faq-${i}`}>
              <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {data.related.length > 0 && (
          <>
            <h2 className="mb-4 mt-12 font-display text-2xl font-bold tracking-tight">Kirim Paket dari {data.origin} ke Kota Lain</h2>
            <div className="flex flex-wrap gap-2">
              {data.related.map((d) => (
                <Link
                  key={d}
                  to={`/paket-kilat-${data.origin.toLowerCase().replace(/\s+/g, "-")}-${d.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border border-border bg-card px-4 py-2 text-sm transition-colors hover:border-primary"
                >
                  {data.origin} → {d}
                </Link>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
